import React, { useMemo } from 'react'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { FragmentType, graphql, useFragment } from '../../../gql'
import { Table } from './Table'
import { SquareAmountCell } from './SquareAmountCell'
import { formatDate } from '../../../lib'
import { PhotoSwipe } from '../../../components/PhotoSwipe'
import { FragmentValue } from '../../../lib/RequestUtils'

export const CompletedReturnsTable_ProductMoveFragment = graphql(`
  fragment CompletedReturnsTable_ProductMoveFragment on ProductMove {
    id
    amount
    productDepotTo {
      position
      expirationDate
      batch
      depot {
        id
        name
      }
    }
    product {
      id
      name
      code
      ean
      mainImage {
        thumbnails {
          format
          url
        }
      }
    }
  }
`)

export const CompletedReturnsTable_ProductMovePackFragment = graphql(`
  fragment CompletedReturnsTable_ProductMovePackFragment on ProductMovePack {
    id
    number
    dateCreated
    moves {
      ...CompletedReturnsTable_ProductMoveFragment
    }
  }
`)

interface CompletedReturnsTableProps {
  returnPack: FragmentType<typeof CompletedReturnsTable_ProductMovePackFragment>
}

const columnHelper = createColumnHelper<FragmentValue<typeof CompletedReturnsTable_ProductMoveFragment>>()

export const CompletedReturnsTable: React.FC<CompletedReturnsTableProps> = ({ returnPack }) => {
  'use no memo'
  const pack = useFragment(CompletedReturnsTable_ProductMovePackFragment, returnPack)
  const moves = useFragment(CompletedReturnsTable_ProductMoveFragment, pack.moves)

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'product',
        header: 'Název zboží',
        cell: ({ row }) => {
          const product = row.original.product
          if (!product) return '-'

          const thumbUrl = product.mainImage?.thumbnails?.find(t => t.format === 'w135')?.url || ''
          const imageUrl = product.mainImage?.thumbnails?.find(t => t.format === 'w2048')?.url || ''

          return (
            <div className="flex items-center gap-2">
              <PhotoSwipe
                alt={product.name}
                caption={`
                ${product.name}<br>
                ${product.code ? `Kód: ${product.code}` : ''}<br>
                ${product.ean ? `EAN: ${product.ean}` : ''}
              `}
                galleryImg={imageUrl}
                src={thumbUrl}
                thumbnails=""
              />
              <span className="font-semibold">{product.name}</span>
            </div>
          )
        },
        // @ts-ignore
        size: '2fr',
      }),
      columnHelper.display({
        id: 'depot',
        header: 'Sklad',
        cell: ({ row }) => row.original.productDepotTo?.depot?.name || '-',
        size: 200,
      }),
      columnHelper.display({
        id: 'code',
        header: 'Kód',
        cell: ({ row }) => row.original.product?.code || '-',
        size: 150,
      }),
      columnHelper.display({
        id: 'ean',
        header: 'EAN',
        cell: ({ row }) => row.original.product?.ean || '-',
        size: 150,
      }),
      columnHelper.display({
        id: 'position',
        header: 'Pozice',
        cell: ({ row }) => row.original.productDepotTo?.position || '-',
        size: 150,
      }),
      columnHelper.display({
        id: 'batch',
        header: 'Šarže',
        cell: ({ row }) => row.original.productDepotTo?.batch || '-',
        size: 150,
      }),
      columnHelper.display({
        id: 'expirationDate',
        header: 'Expirace',
        cell: ({ row }) =>
          row.original.productDepotTo?.expirationDate ? formatDate(row.original.productDepotTo?.expirationDate) : '-',
        size: 150,
      }),
      columnHelper.accessor('amount', {
        header: () => <div className="text-center">Počet ks</div>,
        cell: ({ getValue }) => <SquareAmountCell>{getValue()}</SquareAmountCell>,
        size: 100,
      }),
    ],
    [],
  )

  const columnVisibility = useMemo(
    () => ({
      code: !moves.every(m => !m.product?.code),
      ean: !moves.every(m => !m.product?.ean),
      position: !moves.every(m => m.productDepotTo?.position == '--'),
      batch: !moves.every(m => !m.productDepotTo?.batch),
      expirationDate: !moves.every(m => !m.productDepotTo?.expirationDate),
    }),
    [moves],
  )

  const tableState = useMemo(
    () => ({
      columnVisibility,
    }),
    [columnVisibility],
  )

  const table = useReactTable({
    columns,
    data: moves,
    getCoreRowModel: getCoreRowModel(),
    state: tableState,
  })

  return (
    <div className="flex flex-col gap-4">
      <h3 className="font-bold text-lg text-gray-600">
        Vratka dne {formatDate(pack.dateCreated || new Date())}, příjemka č. {pack.number}
      </h3>
      <div className="shadow-lg rounded-xl">
        <Table table={table} />
      </div>
    </div>
  )
}
